import { useState } from 'react';
import * as React from 'react';

import * as dateFns from 'date-fns';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  TextField,
  Box,
  Stack,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useFinanceContext } from 'src/components/plaid/datasource';
import useLiabilityService from 'src/services/liability-service';
import { useAuthContext } from 'src/auth/hooks';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import { DatePicker } from '@mui/x-date-pickers';
import { capitalizeFirstLetter } from 'src/utils/text-formatter';
import { currencyFormatter } from 'src/utils/currency-formatter';
import normalizeDate from 'src/utils/normalizeDate';
import { balanceForAccount } from 'src/components/plaid/forecast/utils';
import { PercentField } from '../scenarios/PercentField';
import { CurrencyField } from '../scenarios/CurrencyField';
import { liabilityViewModel } from './edit-liabillity-row';
import LiabilityTypeSelector from './liability-type-selector';

const { parseISO, format, addYears } = dateFns;

function ManageLiabilitiesDialog({ open, onClose, liabilities, onSave }) {
  const [allLiabilities, setAllLiabilities] = React.useState(liabilities);
  const [selectedLiability, setSelectedLiability] = React.useState(null);
  const [unsavedLiability, setUnsavedLiability] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const confirmDelete = useBoolean(false);

  React.useEffect(() => {
    setAllLiabilities(liabilities);
  }, [liabilities]);

  const handleSelectLiability = (liability) => {
    setSelectedLiability(liability);
    setUnsavedLiability(null); // Clear any unsaved liability when selecting an existing one
  };

  const handleChange = (key, value) => {
    const updatedLiability = { ...selectedLiability };
    const formatDate = (date) =>
      typeof date === 'string'
        ? date
        : typeof date === 'object'
        ? date.toISOString().split('T')[0]
        : date;

    if (key === 'term') {
      // Handle empty/null values
      if (value === '' || value === null) {
        updatedLiability.term = null;
        updatedLiability.expected_payoff_date = null;
      } else {
        // Update the term and recalculate the expected payoff date
        const termValue = parseInt(value, 10);
        if (!Number.isNaN(termValue)) {
          updatedLiability.term = termValue;

          if (updatedLiability.origination_date) {
            const originationDate = parseISO(formatDate(updatedLiability.origination_date));
            updatedLiability.expected_payoff_date = formatDate(
              dateFns.addYears(originationDate, termValue)
            );
          }
        }
      }
    } else if (key === 'expected_payoff_date') {
      // Update the expected payoff date and recalculate the term
      const payoffDate = parseISO(formatDate(value));
      updatedLiability.expected_payoff_date = formatDate(payoffDate);

      if (updatedLiability.origination_date) {
        const originationDate = parseISO(formatDate(updatedLiability.origination_date));
        updatedLiability.term =
          Math.abs(dateFns.differenceInYears(payoffDate, originationDate)) || 0;
      }
    } else if (key === 'origination_date') {
      // Update the origination date and recalculate the expected payoff date
      const originationDate = parseISO(formatDate(value));
      updatedLiability.origination_date = formatDate(originationDate);

      if (updatedLiability.term) {
        updatedLiability.expected_payoff_date = formatDate(
          dateFns.addYears(originationDate, updatedLiability.term)
        );
      }
    } else {
      updatedLiability[key] = value;
    }

    setSelectedLiability(updatedLiability);
  };

  const handleAddLiability = () => {
    const newLiability = {
      name: '',
      origination_principal_amount: undefined,
      outstanding_balance: undefined,
      interest_rate_percentage: undefined,
      term: undefined,
      origination_date: new Date().toISOString().split('T')[0],
      expected_payoff_date: undefined,
      interest_type: '',
      provider: 'manual',
      type: 'loan',
      subtype: 'generic',
    };
    setUnsavedLiability(newLiability);
    setSelectedLiability(newLiability);
  };

  const isValid = () => {
    const {
      id,
      name,
      term,
      origination_principal_amount,
      origination_date,
      expected_payoff_date,
      outstanding_balance,
      type: _type,
      subtype: _subtype,
      account,
    } = selectedLiability ?? {};

    const type = account?.type ?? _type;
    const subtype = account?.subtype ?? _subtype;

    // obtain a reference to the pre-mutated liability
    const originalLiability =
      liabilities?.find((e) => e.id === id) ?? structuredClone(selectedLiability ?? {});

    // Validation logic
    const validationResults = {
      name: !!name,
      term: term != null,
      origination_principal_amount: origination_principal_amount != null,
      origination_date: !!origination_date,
      expected_payoff_date: !!expected_payoff_date,
      type: !!type,
      subtype: !!subtype,
    };

    // ! early exit if any validation fails
    if (Object.values(validationResults).some((v) => !v)) return false;

    // ? Otherwise we'll proceed with validating dates / values

    // Format dates for consistent comparison
    const formatDate = (date) =>
      dateFns.isValid(normalizeDate(date)) ? format(normalizeDate(date), 'yyyy-MM-dd') : '';

    // Check if dates have changed
    const originationDateChanged =
      formatDate(origination_date) !== formatDate(originalLiability?.origination_date);

    // Handle recalculation and comparison of expected payoff date
    const recalculatedPayoffDate = dateFns.isValid(normalizeDate(origination_date))
      ? format(addYears(normalizeDate(origination_date), term), 'yyyy-MM-dd')
      : '';

    const expectedPayoffDateChanged =
      term !== originalLiability?.term
        ? recalculatedPayoffDate !== formatDate(originalLiability?.expected_payoff_date)
        : formatDate(expected_payoff_date) !== formatDate(originalLiability?.expected_payoff_date);

    const loanTypesChanged =
      type !== originalLiability?.type || subtype !== originalLiability?.subtype;

    // Change detection logic
    const hasChanged = {
      isNew: !originalLiability?.id,
      name: name !== originalLiability?.name,
      term: term !== originalLiability?.term,
      origination_principal_amount:
        origination_principal_amount !== originalLiability?.origination_principal_amount,
      origination_date: originationDateChanged,
      expected_payoff_date: expectedPayoffDateChanged,
      outstanding_balance: outstanding_balance !== originalLiability?.outstanding_balance,
      type: loanTypesChanged,
    };

    return (
      Object.values(validationResults).every(Boolean) && Object.values(hasChanged).some(Boolean)
    );
  };

  const handleRequestDeleteLiability = () => {
    confirmDelete.onTrue();
  };

  const handleDeleteLiability = () => {
    setDeleting(true);
    setTimeout(() => {
      const updatedLiabilities = allLiabilities.filter(
        (liability) => liability.id !== selectedLiability.id
      );
      setAllLiabilities(updatedLiabilities);
      setSelectedLiability(null);
      setDeleting(false);
      confirmDelete.onFalse();
    }, 1000); // Simulate delete delay
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave({ provider: 'manual', ...selectedLiability });
      if (unsavedLiability) {
        setAllLiabilities([...(allLiabilities ?? []), selectedLiability]);
      } else {
        const updatedLiabilities = allLiabilities?.map((liability) =>
          liability.id === selectedLiability.id ? selectedLiability : liability
        );
        setAllLiabilities(updatedLiabilities);
      }
      setSelectedLiability(null);
      setUnsavedLiability(null);
    } catch (error) {
      console.error('Failed to save liability', error.messsage);
    } finally {
      setSaving(false);
    }
  };

  const preventEditing = selectedLiability?.provider !== 'manual';
  const liabilityViewModels = React.useMemo(
    () => (allLiabilities ?? []).map(liabilityViewModel),
    [allLiabilities]
  );

  const canSave = selectedLiability && isValid() && !preventEditing;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            Manage Liabilities
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', height: '80vh' }}>
          {/* Master View */}
          <Box sx={{ flex: 1, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
            <List>
              {liabilityViewModels?.map((liability) => (
                <React.Fragment key={liability.id}>
                  <ListItemButton
                    onClick={() => handleSelectLiability(liability)}
                    selected={selectedLiability?.id === liability.id}
                  >
                    <ListItemText
                      primary={liability.name || 'New Liability'}
                      secondary={`Principal: ${currencyFormatter.format(
                        liability.origination_principal_amount ?? balanceForAccount(liability)
                      )}`}
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddLiability}
                fullWidth
                variant="contained"
                color="info"
              >
                Add Liability
              </Button>
            </Box>
          </Box>

          {/* Detail View */}
          <Box sx={{ flex: 2, p: 2 }}>
            {selectedLiability ? (
              <Stack spacing={2}>
                <Typography variant="h6">Edit Liability</Typography>

                {preventEditing && (
                  <Alert severity="info">
                    This liability is managed by{' '}
                    {capitalizeFirstLetter(selectedLiability?.provider) || 'an external service'}{' '}
                    and cannot be edited.
                  </Alert>
                )}

                <TextField
                  label="Liability Name"
                  placeholder="My Mortgage"
                  value={selectedLiability?.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  fullWidth
                  disabled={preventEditing}
                />

                <LiabilityTypeSelector
                  selectedType={selectedLiability?.type}
                  selectedSubtype={selectedLiability?.subtype}
                  onTypeChange={(value) => handleChange('type', value)}
                  onSubtypeChange={(value) => handleChange('subtype', value)}
                  disabled={preventEditing}
                />

                <CurrencyField
                  label="Principal"
                  placeholder="10,000"
                  value={selectedLiability?.origination_principal_amount}
                  onChange={(e) => handleChange('origination_principal_amount', e.target.value)}
                  fullWidth
                  disabled={preventEditing}
                />
                <CurrencyField
                  label="Outstanding Balance"
                  placeholder="5,000"
                  value={selectedLiability?.outstanding_balance}
                  onChange={(e) => handleChange('outstanding_balance', e.target.value)}
                  fullWidth
                  disabled={preventEditing}
                />

                <PercentField
                  label="Interest Rate (%)"
                  type="number"
                  placeholder={4.5}
                  value={selectedLiability?.interest_rate_percentage}
                  onChange={(e) => handleChange('interest_rate_percentage', e.target.value)}
                  fullWidth
                  disabled={preventEditing}
                />
                <TextField
                  label="Term (years)"
                  type="number"
                  value={selectedLiability?.term ?? ''}
                  placeholder={30}
                  onChange={(e) =>
                    handleChange('term', e.target.value ? Number(e.target.value) : null)
                  }
                  fullWidth
                  disabled={preventEditing}
                />
                <DatePicker
                  label="Effective Date"
                  type="date"
                  value={dateFns.parseISO(selectedLiability?.origination_date)}
                  onChange={(e) => handleChange('origination_date', e)}
                  fullWidth
                  disabled={preventEditing}
                />
                <DatePicker
                  label="Expiration Date"
                  type="date"
                  value={dateFns.parseISO(selectedLiability?.expected_payoff_date)}
                  onChange={(e) => handleChange('expected_payoff_date', e)}
                  fullWidth
                  disabled={true}
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Tooltip title="Delete Liability">
                    <Button
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={handleRequestDeleteLiability}
                      fullWidth
                      disabled={preventEditing}
                    >
                      Delete Liability
                    </Button>
                  </Tooltip>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body1" sx={{ mt: 2 }}>
                Select a liability to edit.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          loading={saving}
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!canSave}
        >
          Save
        </LoadingButton>
      </DialogActions>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={`Delete ${selectedLiability?.name || 'this liability'}?`}
        content={
          <>
            Are you sure you want to delete the{' '}
            <strong>{selectedLiability?.name || 'this liability'}</strong>? This action cannot be
            undone.
          </>
        }
        action={
          <LoadingButton
            loading={deleting}
            variant="contained"
            color="error"
            onClick={handleDeleteLiability}
          >
            Delete
          </LoadingButton>
        }
      />
    </Dialog>
  );
}

export default function ManageLiabilities({ sx }) {
  const [open, setOpen] = useState(false);
  const { liabilities } = useFinanceContext();
  const { user } = useAuthContext();
  const { saveLiability } = useLiabilityService(user?.id);

  const handleToggle = () => setOpen(!open);
  const handleSave = async (updatedLiability) => saveLiability(updatedLiability);

  return (
    <>
      <ManageLiabilitiesDialog
        open={open}
        onClose={handleToggle}
        liabilities={liabilities?.allLiabilities}
        onSave={handleSave}
      />
      <Button onClick={handleToggle} sx={{ ...sx }} color="info" variant="outlined">
        <Stack sx={{ ...sx }}>Manage Liabilities</Stack>
      </Button>
    </>
  );
}
